import React from 'react';
import { motion } from 'framer-motion';

const BRAND = '#1e40af';
const BRAND_SOFT = '#bfdbfe';
const BRAND_DEEP = '#1e3a8a';
const INK = '#1f2937';
const LINE = '#d1d5db';

const easeLoop = [0.42, 0, 0.58, 1];

const SellerAnimation = () => (
  <svg viewBox="0 0 400 200" className="h-full w-full" role="img" aria-label="Seller packing an order">
    <defs>
      <linearGradient id="sellerBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#ffffff" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="400" height="200" fill="url(#sellerBg)" />

    <motion.circle
      cx="320"
      cy="60"
      r="46"
      fill={BRAND_SOFT}
      opacity="0.55"
      animate={{ scale: [1, 1.08, 1] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      style={{ transformOrigin: '320px 60px' }}
    />

    <g transform="translate(100, 70)">
      <rect x="0" y="40" width="140" height="80" rx="4" fill="#fff" stroke={INK} strokeWidth="2" />
      <path d="M0 40 L70 10 L140 40" fill="#fff" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <motion.path
        d="M70 10 L70 40"
        stroke={BRAND}
        strokeWidth="2.5"
        strokeDasharray="140"
        strokeDashoffset="140"
        animate={{ strokeDashoffset: [140, 0, 0, 140] }}
        transition={{ duration: 4, repeat: Infinity, times: [0, 0.4, 0.9, 1], ease: easeLoop }}
      />
      <rect x="56" y="60" width="28" height="4" rx="2" fill={BRAND_SOFT} />
      <rect x="44" y="72" width="52" height="4" rx="2" fill={BRAND_SOFT} />
      <rect x="50" y="84" width="40" height="4" rx="2" fill={BRAND_SOFT} />

      <motion.g
        animate={{ y: [-70, -10, -10, -70], opacity: [0, 1, 1, 0] }}
        transition={{ duration: 4, repeat: Infinity, times: [0, 0.35, 0.85, 1], ease: easeLoop }}
      >
        <rect x="50" y="70" width="40" height="28" rx="3" fill={BRAND} />
        <rect x="56" y="78" width="28" height="3" rx="1.5" fill="#fff" opacity="0.9" />
        <rect x="56" y="84" width="20" height="3" rx="1.5" fill="#fff" opacity="0.7" />
        <rect x="56" y="90" width="24" height="3" rx="1.5" fill="#fff" opacity="0.7" />
      </motion.g>
    </g>

    <motion.g
      animate={{ scale: [0, 1, 1, 0] }}
      transition={{ duration: 4, repeat: Infinity, times: [0.45, 0.6, 0.9, 1], ease: easeLoop }}
      style={{ transformOrigin: '300px 140px' }}
    >
      <circle cx="300" cy="140" r="18" fill={BRAND} />
      <path d="M291 140 L298 147 L310 134" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </motion.g>
  </svg>
);

const PickupAnimation = () => (
  <svg viewBox="0 0 400 200" className="h-full w-full" role="img" aria-label="Pickup truck en route">
    <defs>
      <linearGradient id="pickupBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#ffffff" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="400" height="200" fill="url(#pickupBg)" />

    <line x1="0" y1="160" x2="400" y2="160" stroke={LINE} strokeWidth="2" />
    <motion.g
      animate={{ x: [0, -40] }}
      transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
    >
      {Array.from({ length: 12 }).map((_, i) => (
        <line key={i} x1={i * 40} y1="160" x2={i * 40 + 20} y2="160" stroke={BRAND_SOFT} strokeWidth="2" />
      ))}
    </motion.g>

    {[0, 1, 2].map((i) => (
      <motion.line
        key={i}
        x1="40"
        y1={90 + i * 18}
        x2="110"
        y2={90 + i * 18}
        stroke={BRAND_SOFT}
        strokeWidth="3"
        strokeLinecap="round"
        animate={{ opacity: [0, 1, 0], x: [0, -30] }}
        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2, ease: 'easeOut' }}
      />
    ))}

    <motion.g
      animate={{ y: [0, -2, 0] }}
      transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
    >
      <rect x="140" y="90" width="100" height="60" rx="4" fill="#fff" stroke={INK} strokeWidth="2" />
      <rect x="152" y="104" width="30" height="3" rx="1.5" fill={BRAND_SOFT} />
      <rect x="152" y="114" width="46" height="3" rx="1.5" fill={BRAND_SOFT} />
      <rect x="152" y="124" width="24" height="3" rx="1.5" fill={BRAND_SOFT} />

      <path d="M240 100 L276 100 L290 120 L290 150 L240 150 Z" fill={BRAND} stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <rect x="248" y="108" width="22" height="16" rx="2" fill="#fff" opacity="0.9" />

      <circle cx="172" cy="156" r="10" fill={INK} />
      <circle cx="268" cy="156" r="10" fill={INK} />
      <motion.g
        animate={{ rotate: 360 }}
        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '172px 156px' }}
      >
        <line x1="172" y1="148" x2="172" y2="164" stroke={BRAND_SOFT} strokeWidth="2" />
      </motion.g>
      <motion.g
        animate={{ rotate: 360 }}
        transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
        style={{ transformOrigin: '268px 156px' }}
      >
        <line x1="268" y1="148" x2="268" y2="164" stroke={BRAND_SOFT} strokeWidth="2" />
      </motion.g>
    </motion.g>
  </svg>
);

const HubAnimation = () => (
  <svg viewBox="0 0 400 200" className="h-full w-full" role="img" aria-label="Warehouse hub sorting parcels">
    <defs>
      <linearGradient id="hubBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#ffffff" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="400" height="200" fill="url(#hubBg)" />

    <g opacity="0.7">
      {[0, 1, 2].map((row) => (
        <g key={row} transform={`translate(40, ${30 + row * 28})`}>
          <line x1="0" y1="0" x2="320" y2="0" stroke={LINE} strokeWidth="2" />
          {[0, 1, 2, 3, 4, 5].map((col) => (
            <rect key={col} x={col * 54 + 6} y={-14} width="42" height="14" rx="2" fill="#fff" stroke={LINE} strokeWidth="1.5" />
          ))}
        </g>
      ))}
    </g>

    <rect x="20" y="140" width="360" height="18" rx="4" fill="#fff" stroke={INK} strokeWidth="2" />
    <motion.g
      animate={{ x: [0, -24] }}
      transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
    >
      {Array.from({ length: 18 }).map((_, i) => (
        <line key={i} x1={20 + i * 24} y1="149" x2={36 + i * 24} y2="149" stroke={BRAND_SOFT} strokeWidth="2" />
      ))}
    </motion.g>

    {[0, 1, 2].map((i) => (
      <motion.g
        key={i}
        animate={{ x: [-40, 360] }}
        transition={{ duration: 3.6, repeat: Infinity, delay: i * 1.2, ease: 'linear' }}
      >
        <rect x="40" y="118" width="26" height="22" rx="2" fill={i === 1 ? BRAND : '#fff'} stroke={INK} strokeWidth="2" />
        <line x1="53" y1="118" x2="53" y2="140" stroke={i === 1 ? '#fff' : INK} strokeWidth="1.5" opacity="0.8" />
        <line x1="40" y1="129" x2="66" y2="129" stroke={i === 1 ? '#fff' : INK} strokeWidth="1.5" opacity="0.8" />
      </motion.g>
    ))}

    <motion.g
      animate={{ opacity: [0.3, 1, 0.3] }}
      transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <circle cx="370" cy="129" r="6" fill={BRAND} />
    </motion.g>
  </svg>
);

const DeliveryAnimation = () => (
  <svg viewBox="0 0 400 200" className="h-full w-full" role="img" aria-label="Package delivered at doorstep">
    <defs>
      <linearGradient id="deliveryBg" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#eff6ff" />
        <stop offset="100%" stopColor="#ffffff" />
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="400" height="200" fill="url(#deliveryBg)" />

    <line x1="0" y1="168" x2="400" y2="168" stroke={LINE} strokeWidth="2" />

    <g transform="translate(220, 50)">
      <path d="M-10 50 L60 10 L130 50 L130 120 L-10 120 Z" fill="#fff" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
      <path d="M-20 52 L60 4 L140 52" fill="none" stroke={BRAND} strokeWidth="2.5" strokeLinejoin="round" />
      <rect x="46" y="70" width="28" height="50" rx="2" fill={BRAND_SOFT} stroke={INK} strokeWidth="2" />
      <circle cx="66" cy="96" r="2" fill={INK} />
      <rect x="0" y="70" width="20" height="20" rx="2" fill="none" stroke={INK} strokeWidth="1.5" />
      <rect x="100" y="70" width="20" height="20" rx="2" fill="none" stroke={INK} strokeWidth="1.5" />
    </g>

    <motion.g
      animate={{
        x: [-180, -30, -30, -30],
        y: [0, 0, 0, 0],
        opacity: [0, 1, 1, 1]
      }}
      transition={{ duration: 4, repeat: Infinity, times: [0, 0.35, 0.9, 1], ease: easeLoop }}
    >
      <g transform="translate(240, 152)">
        <rect x="0" y="0" width="40" height="30" rx="2" fill={BRAND} stroke={INK} strokeWidth="2" />
        <line x1="20" y1="0" x2="20" y2="30" stroke="#fff" strokeWidth="1.5" opacity="0.9" />
        <line x1="0" y1="15" x2="40" y2="15" stroke="#fff" strokeWidth="1.5" opacity="0.9" />
      </g>
    </motion.g>

    <motion.g
      animate={{ scale: [0, 1.3, 1, 0] }}
      transition={{ duration: 4, repeat: Infinity, times: [0.4, 0.55, 0.9, 1], ease: easeLoop }}
      style={{ transformOrigin: '80px 70px' }}
    >
      <circle cx="80" cy="70" r="24" fill="none" stroke={BRAND} strokeWidth="2" opacity="0.5" />
    </motion.g>
    <motion.g
      animate={{ scale: [0, 1, 1, 0] }}
      transition={{ duration: 4, repeat: Infinity, times: [0.35, 0.55, 0.9, 1], ease: easeLoop }}
      style={{ transformOrigin: '80px 70px' }}
    >
      <path d="M80 50 C70 50 62 58 62 68 C62 82 80 96 80 96 C80 96 98 82 98 68 C98 58 90 50 80 50 Z" fill={BRAND} />
      <circle cx="80" cy="68" r="6" fill="#fff" />
    </motion.g>

    <motion.g
      animate={{ scale: [0, 1, 1, 0], opacity: [0, 1, 1, 0] }}
      transition={{ duration: 4, repeat: Infinity, times: [0.65, 0.78, 0.92, 1], ease: easeLoop }}
      style={{ transformOrigin: '310px 60px' }}
    >
      <circle cx="310" cy="60" r="16" fill={BRAND_DEEP} />
      <path d="M302 60 L308 66 L318 54" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </motion.g>
  </svg>
);

const WorkflowAnimations = {
  Seller: SellerAnimation,
  Pickup: PickupAnimation,
  Hub: HubAnimation,
  Delivery: DeliveryAnimation,
  Shipped: HubAnimation,
  OFD: PickupAnimation,
  Delivered: DeliveryAnimation
};

export default WorkflowAnimations;
